import React, { useEffect, useState } from 'react'
import { ValidatorState } from '@chegg-wt/core/state/citation/types'
import { SourceType } from '@chegg-wt/core/types/core'
import { CitationData } from '@chegg-wt/core/types/form'
import { useMutation } from '@apollo/client'
import { flattenData } from '@chegg-wt/core/utils/citations'
import { newProjectName } from '@chegg-wt/core/utils/projects'
import { CREATE_CITATION, UPDATE_CITATION } from '@chegg-wt/core/queries/Citations'
import {
  CreateCitation,
  CreateCitationVariables,
} from '@chegg-wt/core/queries/CreateCitation'
import {
  UpdateCitation,
  UpdateCitationVariables,
} from '@chegg-wt/core/queries/UpdateCitation'
import {
  CreateProject,
  CreateProjectVariables,
} from '@chegg-wt/core/queries/CreateProject'
import { CREATE_PROJECT } from '@chegg-wt/core/queries/Project'
import { useDispatch, useSelector } from 'react-redux'
import { FormType, FormTypes } from '@chegg-wt/core/constants/form'
import { NEW_PROJECT_ID } from '@chegg-wt/core/constants/projects'
import {
  citationCreateSucceeded,
  citationUpdateSucceeded,
  citationValidated,
} from '@chegg-wt/core/state/citation/actions'
import {
  CLICKSTREAM_CREATE_CITATION,
  CLICKSTREAM_STATE,
  emitSuccessEvent,
} from '@chegg-wt/core/state/instrumentation/actions'
import { styleIdToAlias, transformDeprecatedStyleId } from '@chegg-wt/core/utils/styles'
import {
  getCreateCitationMetadata,
  getUpdateCitationMetadata,
} from '@chegg-wt/core/utils/plusCiteRioEvent'
import {
  currentCitationAutociteRioTrackingIDSelector,
  currentCitationAutociteResponseForRioSelector,
} from '@chegg-wt/core/state/citation/selectors'
import {
  autociteApiVersionSelector,
  autociteV0RioOptFlagSelector,
} from '@chegg-wt/core/state/createCitation/selectors'
import { autociteSources } from '@chegg-wt/core/constants/citationFlow'
import {
  citationToFormStateTransformation,
  formStateToCitationTransformation,
  getFormMode,
  getFormsBackButtonText,
} from '@chegg-wt/core/components/Layouts/Form/utils'
import WritingForms, {
  IntialCitationData,
  SourceType as FormSourceType,
  PubType,
} from '@chegg/writing-forms'
import { sourceTypeById } from '@chegg-wt/core/utils/sourceTypes'
import { useCapitalization } from '@chegg-wt/core/hooks/useCapitalization'
import { CITATION_FORM } from './utils/testIds'
import * as Styled from './styled'

const createProjectVariables = (defaultStyle) => ({
  name: newProjectName(),
  public: false,
  defaultStyle,
})

interface CitationFormProps {
  citationStyle: string
  sourceType: SourceType
  citation?: any
  projectId: string
  formType: FormType
  onChangeSourceType: (sourceType: SourceType) => void
  onComplete: (citationId: string, status: ValidatorState, projectId?: string) => void
  onBack: () => void
}

const CitationFormWrapper: React.FC<{ classname: string }> = ({
  children,
  classname,
}) => {
  return <div className={classname}>{children}</div>
}

const CitationForm: React.FC<CitationFormProps> = ({
  citationStyle: style,
  sourceType,
  citation,
  projectId,
  formType,
  onChangeSourceType,
  onComplete,
  onBack,
}) => {
  const dispatch = useDispatch()

  const [initialCitationData, setInitialCitationData] = useState<IntialCitationData>({
    data: {
      source:
        sourceType &&
        (sourceTypeById(sourceType.id.toLowerCase()).apiAlias as FormSourceType),
    },
    annotation: '',
  })

  const [createProject] = useMutation<CreateProject, CreateProjectVariables>(
    CREATE_PROJECT,
    { variables: createProjectVariables(style) }
  )

  const [addCitation] = useMutation<CreateCitation, CreateCitationVariables>(
    CREATE_CITATION
  )
  const [editCitation] = useMutation<UpdateCitation, UpdateCitationVariables>(
    UPDATE_CITATION
  )

  const autociteRioTrackingID = useSelector(currentCitationAutociteRioTrackingIDSelector)
  const autociteResponseForRio = useSelector(
    currentCitationAutociteResponseForRioSelector
  )
  const autociteV0RioOptFlag = useSelector(autociteV0RioOptFlagSelector)
  const autociteApiVersion = useSelector(autociteApiVersionSelector)

  const handleSubmit = async (formCitation: CitationData) => {
    const { contributors, ...formCitationData } = formCitation
    dispatch(citationValidated(formCitation.validatorStatus))

    switch (formType) {
      case FormTypes.NEW:
      case FormTypes.MANUAL: {
        let newProjectId: string
        if (projectId === NEW_PROJECT_ID) {
          const { data } = await createProject()
          newProjectId = data.createProject.id
        }
        const { data } = await addCitation({
          variables: {
            projectId: newProjectId || projectId,
            pubType: formCitationData.pubtype.main,
            sourceType: formCitationData.source,
            annotation: formCitationData.annotation,
            contributors,
            data: formCitationData,
          },
        })

        const citationMetadata =
          (autociteV0RioOptFlag || autociteApiVersion) && autociteRioTrackingID
            ? getCreateCitationMetadata(
                formCitation,
                autociteResponseForRio,
                autociteRioTrackingID,
                data.createCitation.citationId
              )
            : undefined
        // trigger rio/gtm/optimizely events
        dispatch(
          citationCreateSucceeded({
            citation: flattenData(data.createCitation),
            citationMetadata,
          })
        )
        dispatch(
          emitSuccessEvent({
            viewName: CLICKSTREAM_CREATE_CITATION,
            state: CLICKSTREAM_STATE.success,
            style: styleIdToAlias(transformDeprecatedStyleId(style)),
          })
        )

        onComplete(
          data.createCitation.citationId,
          formCitation.validatorStatus,
          newProjectId
        )
        break
      }
      case FormTypes.EDIT: {
        const { data } = await editCitation({
          variables: {
            projectId,
            citationId: citation.id,
            pubType: formCitationData.pubtype.main,
            sourceType: formCitationData.source,
            annotation: formCitationData.annotation,
            contributors,
            data: formCitationData,
          },
        })
        const citationMetadata =
          (autociteV0RioOptFlag || autociteApiVersion) &&
          autociteSources.includes(formCitation.source)
            ? getUpdateCitationMetadata(formCitation, citation.id)
            : undefined
        // trigger rio/gtm events
        dispatch(
          citationUpdateSucceeded({
            citation: flattenData(data.updateCitation),
            citationMetadata,
          })
        )
        onComplete(citation.citationId, formCitation.validatorStatus)
        break
      }
      default:
    }
  }

  const { citationWithCapitalizedTitle } = useCapitalization(formType, citation)

  useEffect(() => {
    if (citationWithCapitalizedTitle) {
      setInitialCitationData(
        formStateToCitationTransformation(citationWithCapitalizedTitle)
      )
    }
  }, [formType, citationWithCapitalizedTitle])

  const onFormSourceUpdate = (source: string, pubTypeData?: PubType): void => {
    onChangeSourceType(sourceTypeById(source))
    const initialCitationDataForSourceUpdate: IntialCitationData = JSON.parse(
      JSON.stringify(initialCitationData)
    ) // creating a deep copy
    initialCitationDataForSourceUpdate.data.source = source.toLowerCase() as FormSourceType
    if (pubTypeData) {
      initialCitationDataForSourceUpdate.data.pubtype = pubTypeData
    }
    if (
      formType &&
      formType === FormTypes.EDIT &&
      initialCitationData.data?.source === FormSourceType.BOOK &&
      initialCitationData.data.pubtype
    ) {
      initialCitationDataForSourceUpdate.data.pubtype = {
        ...initialCitationData.data.pubtype,
      }
    }
    setInitialCitationData(initialCitationDataForSourceUpdate)
  }

  return (
    <Styled.Container data-test={CITATION_FORM}>
      <CitationFormWrapper classname="">
        <Styled.WritingFormsWrapper>
          {/* writingForms package integration */}
          <WritingForms
            mode={getFormMode(formType)}
            intialCitationState={initialCitationData}
            onSave={(citation: any) =>
              handleSubmit(citationToFormStateTransformation(citation))
            }
            onCancel={onBack}
            onSourceChange={onFormSourceUpdate}
            className="wt-writing-forms-container"
            backButtonText={getFormsBackButtonText(formType, sourceType.value)}
          />
        </Styled.WritingFormsWrapper>
      </CitationFormWrapper>
    </Styled.Container>
  )
}

export default CitationForm
